/**
 * Fixed rebate fixtures for Storybook snapshots.
 *
 * Mirrors the QA panel's rebate builder but with FIXED dates instead of
 * Date.now() offsets — snapshot stories must render pixel-identical on any day.
 */

import { buildPortalAssetUrl } from 'shared/utils/helpers';
import {
  CAMPAIGN_LEVELS,
  REBATE_CAMPAIGN_TYPE,
  type RebateStatuses,
  type IRebateCampaign,
  type IRebateResources,
} from '../types/campaign.types';
import { STORY_ACTIVE_END, STORY_ACTIVE_START } from './campaignStoryHelpers';

const FILL_COLOR = '#06230C';
const BORDER_COLOR = '#2F980F';

const res = (key: string) => buildPortalAssetUrl(`/campaigns/rebate/Default/${key}.webp`);
const resMulti = (key: string) => buildPortalAssetUrl(`/campaigns/rebate/MultiLobby/${key}.webp`);

// fallow-ignore-next-line unused-export
export const mockRebateResources: IRebateResources = {
  CampaignsMultiViewSoloSeparatedBackgroundLandscape: res('CampaignsMultiViewSoloSeparatedBackgroundLandscape'),
  CampaignsMultiViewSoloSeparatedBackgroundPortrait: res('CampaignsMultiViewSoloSeparatedBackgroundPortrait'),
  CampaignsMultiViewSoloTransparentImage: res('CampaignsMultiViewSoloTransparentImage'),
  CampaignsMultiViewSoloTransparentImageDesktop: res('CampaignsMultiViewSoloTransparentImageDesktop'),
  CampaignsPlate: res('CampaignsPlate'),
  CampaignsPlateClose: res('CampaignsPlateClose'),
  CampaignsPlateCloseLogo: res('CampaignsPlateCloseLogo'),
  CampaignsSingleViewSoloBackgroundClose: res('CampaignsSingleViewSoloBackgroundClose'),
  CampaignsSingleViewSoloBackgroundLandscape: res('CampaignsSingleViewSoloBackgroundLandscape'),
  CampaignsSingleViewSoloBackgroundPortrait: res('CampaignsSingleViewSoloBackgroundPortrait'),
  CampaignsMultiViewPlate: res('CampaignsMultiViewPlate'),
  FillColor: FILL_COLOR,
  BorderColor: BORDER_COLOR,
};

/**
 * Mixed-folder resource bundle for the second rebate mock — multi-view keys
 * from MultiLobby/, everything else from Default/. Mirrors the PrizePop
 * pattern in campaignMockData.ts (mockCampaignDataMulti2) to test rendering
 * when a campaign's BE response pulls from different skin folders.
 */
const mockRebateResourcesMulti: IRebateResources = {
  CampaignsMultiViewSoloSeparatedBackgroundLandscape: resMulti('CampaignsMultiViewSoloSeparatedBackgroundLandscape'),
  CampaignsMultiViewSoloSeparatedBackgroundPortrait: resMulti('CampaignsMultiViewSoloSeparatedBackgroundPortrait'),
  CampaignsMultiViewSoloTransparentImage: resMulti('CampaignsMultiViewSoloTransparentImage'),
  CampaignsMultiViewSoloTransparentImageDesktop: resMulti('CampaignsMultiViewSoloTransparentImageDesktop'),
  CampaignsPlate: res('CampaignsPlate'),
  CampaignsPlateClose: res('CampaignsPlateClose'),
  CampaignsPlateCloseLogo: res('CampaignsPlateCloseLogo'),
  CampaignsSingleViewSoloBackgroundClose: res('CampaignsSingleViewSoloBackgroundClose'),
  CampaignsSingleViewSoloBackgroundLandscape: res('CampaignsSingleViewSoloBackgroundLandscape'),
  CampaignsSingleViewSoloBackgroundPortrait: res('CampaignsSingleViewSoloBackgroundPortrait'),
  CampaignsMultiViewPlate: res('CampaignsMultiViewPlate'),
  FillColor: FILL_COLOR,
  BorderColor: BORDER_COLOR,
};

// fallow-ignore-next-line unused-export
export const mockRebateCashbackLevels = [
  { levelNumber: 1, betRangeFrom: 0, betRangeTo: 1000, rebatePercent: 1, maxPayoutAmount: 10 },
  { levelNumber: 2, betRangeFrom: 1000.01, betRangeTo: 7000, rebatePercent: 1.5, maxPayoutAmount: 90 },
  { levelNumber: 3, betRangeFrom: 7000.01, betRangeTo: 13000, rebatePercent: 2.0, maxPayoutAmount: 120 },
];

// fallow-ignore-next-line unused-export
export const mockRebateUserProgress = {
  spentBetAmount: 9450,
  levelProgress: 32,
  claimedWin: 10,
  totalWinAmount: 55,
};

export const mockRebateData: IRebateCampaign = {
  id: 'story-rebate-1',
  name: 'Ultimate Winter Rebate',
  terms: 'Play eligible games during the campaign period to earn cashback. Minimum bet requirements apply.',
  status: 'ACTIVE' as RebateStatuses,
  startDate: STORY_ACTIVE_START,
  endDate: STORY_ACTIVE_END,
  visibleEndDate: STORY_ACTIVE_END,
  maxPayoutAmount: 220,
  level: CAMPAIGN_LEVELS.GOLD,
  games: ['BCSlot', 'BGSlot', 'BGIBSlot', 'SVSlot'],
  minStake: 25,
  baseCurrency: 'EUR',
  joined: true,
  resources: mockRebateResources,
  minPayoutAmount: 2,
  minWinBetAmount: 300,
  userProgress: mockRebateUserProgress,
  cashbackLevels: mockRebateCashbackLevels,
  type: REBATE_CAMPAIGN_TYPE,
} as IRebateCampaign;

export const mockRebateDataMulti: IRebateCampaign = {
  id: 'story-rebate-2',
  name: 'Weekend Cashback Rebate',
  terms: 'Earn cashback on your weekend spins. Higher bets unlock higher rebate percentages.',
  status: 'PROMO' as RebateStatuses,
  startDate: STORY_ACTIVE_START,
  endDate: STORY_ACTIVE_END,
  visibleEndDate: STORY_ACTIVE_END,
  maxPayoutAmount: 150,
  level: CAMPAIGN_LEVELS.PURPLE,
  games: ['SACBLSlot', 'OHSHSlot', 'TNCLHSlot', 'FYHBSlot'],
  minStake: 50,
  baseCurrency: 'EUR',
  joined: false,
  resources: mockRebateResourcesMulti,
  minPayoutAmount: 1,
  minWinBetAmount: 200,
  userProgress: null,
  cashbackLevels: [
    { levelNumber: 1, betRangeFrom: 0, betRangeTo: 500, rebatePercent: 0.5, maxPayoutAmount: 5 },
    { levelNumber: 2, betRangeFrom: 501, betRangeTo: 3000, rebatePercent: 1.0, maxPayoutAmount: 30 },
  ],
  type: REBATE_CAMPAIGN_TYPE,
} as IRebateCampaign;
